import React, { useEffect, useState } from "react";
import { createStandardCourse, linkStandardCourseAhaProduct, listStandardCourses } from "../../../api/backend";
import { Modal, Notice, StatePanel } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { errorText } from "./common";

const keyToNumber = (key) => `AHA-${key.split("_").slice(0, 2).join("-")}`;

/** Creates a draft v1 for a catalog product that has no course yet, and links the product to it. */
export function CreateFromProductDialog({ product, onClose, onDone }) {
  const [title, setTitle] = useState(/^AHA\b/i.test(product.name) ? product.name : `AHA ${product.name}`);
  const [number, setNumber] = useState(keyToNumber(product.key));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const create = async () => {
    setSaving(true);
    setError(null);
    let created;
    try {
      created = await createStandardCourse({ title: title.trim(), courseNumber: number.trim(), category: "AHA" });
    } catch (e) {
      setError(errorText(e, "The course could not be created."));
      setSaving(false);
      return;
    }
    try {
      await linkStandardCourseAhaProduct(created.id, product.key);
    } catch (e) {
      // The course exists; the admin can link the product from its Integrations tab.
      onDone(created.id, `Draft v1 created, but ${product.name} could not be linked: ${errorText(e, "try again from the Integrations tab.")}`);
      return;
    }
    onDone(created.id, `Draft v1 created and ${product.name} linked.`);
  };

  return (
    <Modal title="Create standard course from product" sub={`${product.name} · ${product.environment?.productNumber || product.key}`} labelId="create-from-product-title"
      onClose={() => !saving && onClose()}
      footer={(
        <>
          <button type="button" className="b ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="b pri" disabled={saving || !title.trim()} onClick={create}>{saving ? "Creating…" : "Create draft and link"}</button>
        </>
      )}
    >
      <p className="cons sub" style={{ fontSize: 13.5 }}>
        Creates draft v1 with {product.name} already linked. Prefilled from the product; change anything before creating. Not visible to centers until you publish.
      </p>
      <label className="field">Title<span className="inp"><input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></span></label>
      <label className="field">Course number<span className="inp"><input className="mono" value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} /></span></label>
      {error && <Notice kind="bad">{error}</Notice>}
    </Modal>
  );
}

/** Links a catalog product to an existing active course. */
export function LinkCourseDialog({ product, onClose, onDone }) {
  const [courses, setCourses] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listStandardCourses()
      .then((list) => { if (!cancelled) setCourses((Array.isArray(list) ? list : []).filter((c) => c.status !== "RETIRED")); })
      .catch((e) => { if (!cancelled) { setCourses([]); setError(errorText(e, "The courses could not be loaded.")); } });
    return () => { cancelled = true; };
  }, []);

  const list = (courses || []).filter((c) => `${c.title} ${c.courseNumber || ""}`.toLowerCase().includes(query.trim().toLowerCase()));

  const link = async () => {
    setSaving(true);
    setError(null);
    try {
      await linkStandardCourseAhaProduct(selected.id, product.key);
      onDone(selected.id, `${product.name} linked to ${selected.title}.`);
    } catch (e) {
      setError(errorText(e, "The product could not be linked."));
      setSaving(false);
    }
  };

  return (
    <Modal title="Link to existing course" sub={`${product.name} · ${product.environment?.productNumber || product.key}`} labelId="link-course-title"
      onClose={() => !saving && onClose()}
      footer={(
        <>
          <span className="note">Environment status is unchanged.</span>
          <button type="button" className="b ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="b pri" disabled={!selected || saving} onClick={link}><I.link s={13} />Link course</button>
        </>
      )}
    >
      {error && <Notice kind="bad">{error}</Notice>}
      <div className="srch" style={{ maxWidth: "none" }}>
        <I.search s={15} />
        <input placeholder="Search courses" aria-label="Search courses" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      </div>
      <div className="panel" role="radiogroup" aria-label="Course" style={{ overflow: "auto", maxHeight: 300 }}>
        {courses === null ? <StatePanel kind="loading" what="courses" />
          : list.length === 0 ? <div className="state" style={{ padding: 22 }}><b>No courses match</b></div>
            : list.map((c, i) => (
              <label key={c.id} className="pk-row" style={{ borderTop: i ? "1px solid var(--ga-line)" : 0, cursor: "pointer", background: selected?.id === c.id ? "var(--ga-soft)" : "#fff" }}>
                <input type="radio" name="link-course" checked={selected?.id === c.id} onChange={() => setSelected(c)} style={{ accentColor: "var(--color-primary)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontWeight: 600 }}>{c.title}</b>
                  {c.courseNumber && <div style={{ marginTop: 3 }}><span className="code">{c.courseNumber}</span></div>}
                </div>
              </label>
            ))}
      </div>
    </Modal>
  );
}
